/**
 * Manage Data Menu Component
 *
 * Dropdown menu for Export/Import/Save actions to clean up the UI.
 */

import {
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  FileDownload as FileDownloadIcon,
  FileUpload as FileUploadIcon,
  Save as SaveIcon,
  TableChart as TableChartIcon,
  Description as DescriptionIcon,
  Link as LinkIcon,
  Code as CodeIcon,
} from '@mui/icons-material';
import { useState } from 'react';

interface ManageDataMenuProps {
  onExportCSV: () => void;
  onExportJSON: () => void;
  onImportJSON: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onSaveScenario: () => void;
  onShareConfiguration?: () => void;
  onExportMarkdown?: () => void;
  scenariosCount: number;
  disableButtons?: boolean;
}

export function ManageDataMenu({
  onExportCSV,
  onExportJSON,
  onImportJSON,
  onSaveScenario,
  onShareConfiguration,
  onExportMarkdown,
  scenariosCount,
  disableButtons = false,
}: ManageDataMenuProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleAction = (action: () => void) => {
    action();
    handleClose();
  };

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        onClick={handleClick}
        startIcon={<FileDownloadIcon />}
        id="manage-data-button"
        aria-controls={open ? 'manage-data-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
      >
        Manage Data
      </Button>
      <Menu
        id="manage-data-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'manage-data-button',
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <MenuItem onClick={() => handleAction(onExportCSV)} disabled={disableButtons || scenariosCount === 0}>
          <ListItemIcon>
            <TableChartIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Export as CSV</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleAction(onExportJSON)} disabled={disableButtons || scenariosCount === 0}>
          <ListItemIcon>
            <DescriptionIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Export as JSON</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => {
          handleAction(() => {
            const input = document.createElement('input');
            input.type = 'file';
            input.accept = '.json';
            input.onchange = (e) => {
              const target = e.target as HTMLInputElement;
              if (target.files) {
                onImportJSON({ target } as any);
              }
            };
            input.click();
          });
        }} disabled={disableButtons || scenariosCount >= 20}>
          <ListItemIcon>
            <FileUploadIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Import from JSON</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => handleAction(onShareConfiguration!)} disabled={disableButtons || !onShareConfiguration}>
          <ListItemIcon>
            <LinkIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Copy Share Link</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleAction(onExportMarkdown!)} disabled={disableButtons || !onExportMarkdown || scenariosCount === 0}>
          <ListItemIcon>
            <CodeIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Copy as Markdown Table</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => handleAction(onSaveScenario)} disabled={disableButtons}>
          <ListItemIcon>
            <SaveIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Save Current as Scenario</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
}
